import printJS from "print-js";
import html2pdf from "html2pdf.js";

import type { KotPrinterChannel } from '@src/types/kotLogging';

export type { KotPrinterChannel };

export interface KotPrintResult {
    channel: KotPrinterChannel;
}

// Function to detect if running inside React Native WebView
const isWebView = () => {
    return !!(window as any).ReactNativeWebView;
};

// Helper to get user's saved printer for a type (main/kitchen) from localStorage
export async function getUserKOTPrinter(type: "main" | "kitchen") {
    try {
        const savedPrinters = JSON.parse(localStorage.getItem("KOT_PRINTER_CONFIG") || '{}');
        return savedPrinters[type] || null;
    } catch (error) {
        console.error('Error loading printer config from localStorage:', error);
        return null;
    }
}

/** Resolve which kitchen print path will be used (for confirm UX / logging). */
export async function resolveKitchenPrintChannel(): Promise<KotPrinterChannel> {
    if (isWebView()) {
        return 'webview';
    }

    const printerId = await getUserKOTPrinter('kitchen');
    if (
        printerId &&
        printerId !== 'NO_PRINTER' &&
        (window.navigator as any)?.bluetooth
    ) {
        return 'bluetooth';
    }

    return 'browser';
}

// Function to print using a Bluetooth thermal printer
async function handleBluetoothPrintToDevice(printerId: string, htmlContent: string, orderId: string) {
    try {
        // Check if Web Bluetooth is supported
        if (!(window.navigator as any)?.bluetooth) {
            console.log('Web Bluetooth not supported, falling back to browser print');
            handleBrowserPrintWithoutNewWindow(htmlContent, orderId);
            return;
        }

        // Try to connect to the specific configured printer
        let device;
        try {
            // First, try to get already connected devices
            const devices = await (window.navigator as any).bluetooth.getDevices();
            device = devices.find((d: any) => d.id === printerId || d.name === printerId);
            
            // if (!device) {
            //     // If not found in connected devices, try to request it specifically
            //     device = await (window.navigator as any).bluetooth.requestDevice({
            //         filters: [
            //             { name: printerId },
            //             { namePrefix: printerId },
            //             { services: ['000018f0-0000-1000-8000-00805f9b34fb'] }
            //         ],
            //         optionalServices: [
            //             '000018f0-0000-1000-8000-00805f9b34fb', // Common thermal printer service UUID
            //             '00001800-0000-1000-8000-00805f9b34fb', // Generic Access service
            //             '00001801-0000-1000-8000-00805f9b34fb'  // Generic Attribute service
            //         ]
            //     });
            // }
        } catch (error) {
            console.log('Could not find configured printer, falling back to device picker');
            // Fallback to device picker if configured printer not found
            // device = await (window.navigator as any).bluetooth.requestDevice({
            //     acceptAllDevices: true,
            //     optionalServices: [
            //         '000018f0-0000-1000-8000-00805f9b34fb',
            //         '00001800-0000-1000-8000-00805f9b34fb',
            //         '00001801-0000-1000-8000-00805f9b34fb'
            //     ]
            // });
            handleBrowserPrintWithoutNewWindow(htmlContent, orderId);
        }

        console.log('Connecting to printerId:', printerId);
        console.log('Connecting to printerName:', device?.name);

        // Connect to the device
        const server = await device?.gatt?.connect();
        if (!server) {
            throw new Error("Failed to connect to Bluetooth device");
        }

        // Get the primary service (try common thermal printer service first)
        let service;
        try {
            service = await server.getPrimaryService('000018f0-0000-1000-8000-00805f9b34fb');
        } catch (error) {
            // Try generic service if thermal printer service not found
            service = await server.getPrimaryService('00001800-0000-1000-8000-00805f9b34fb');
        }

        // Get the characteristic for writing data
        const characteristic = await service.getCharacteristic('00002af1-0000-1000-8000-00805f9b34fb');

        // Convert HTML content to plain text for thermal printer
        const textContent = htmlContent
            .replace(/<[^>]*>/g, '') // Remove HTML tags
            .replace(/&nbsp;/g, ' ') // Replace HTML entities
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'");

        // Add printer commands for thermal printer
        const printCommands = [
            '\x1B\x40', // Initialize printer
            '\x1B\x61\x01', // Center alignment
            textContent,
            '\n\n\n', // Feed paper
            '\x1B\x69' // Cut paper (if supported)
        ].join('');

        // Convert text to bytes and send to printer
        const encoder = new TextEncoder();
        const data = encoder.encode(printCommands);
        
        await characteristic.writeValue(data);
        
        console.log('Successfully printed to configured printer:', device?.name);
        
        // Disconnect from the device
        server.disconnect();
        
    } catch (error) {
        console.error('Bluetooth printing failed:', error);
        
        // Fallback to browser print
        console.log('Falling back to browser print');
        handleBrowserPrintWithoutNewWindow(htmlContent, orderId);
    }
}

const handleBrowserPrintWithoutNewWindow = (htmlContent: any, orderId: any = "Test") => {
    printJS({
        printable: htmlContent,
        type: "raw-html", // Available
        documentTitle: `Order - ${orderId}`, // Set dynamic document name
        scanStyles: false, // Set true if you want to include global styles
    });
}
// Fallback: Function to print using browser print
// const handleBrowserPrint = (htmlContent: any, orderId: any="Test") => {
//     // console.log('-----', htmlContent);
//     // if (printRef.current) {
//     const printWindow = window.open("", "_blank");
//     if (printWindow) {
//         printWindow.document.open();
//         printWindow.document.write(`
//           <html>
//             <head>
//               <title>Order No - ${orderId}</title>
//               <style>
//                 body { font-family: Arial, sans-serif; font-size: 14px; }
//                 .kot-container { padding: 10px; max-width: 300px; }
//               </style>
//             </head>
//             <body onload="window.print(); setTimeout(() => window.close(), 0);">
//               <div class="kot-container">${htmlContent}</div>
//             </body>
//           </html>
//         `);
//         printWindow.document.close(); // TODO: enable it
//     }
//     // }
// };

// Function to send message to React Native WebView for native printing
export const printPDF = async (htmlContent: any, orderId: any) => {
    //   const printRef = useRef<HTMLDivElement>(null);
    if (isWebView()) {
        (window as any).ReactNativeWebView.postMessage(
            // JSON.stringify({ type: "PRINT_KOT", content: htmlContent })
            JSON.stringify({ type: "print", data: htmlContent || "<p>Error...Error...</p>" })
        );
    } else {
        // handleBluetoothPrint(htmlContent, orderId); // If not in WebView, use normal Bluetooth print
        // handleBrowserPrint(htmlContent, orderId);
        handleBrowserPrintWithoutNewWindow(htmlContent, orderId);
        
    }
};

export const downloadPDF = async (htmlContent: any, orderId: any) => {
    const element = document.createElement("div");
    element.style.padding = "20px";
    element.innerHTML = htmlContent;

    // Wait for all images inside the element to load
    await Promise.all(
        Array.from(element.querySelectorAll("img")).map((img) => {
            if (img.complete) return Promise.resolve();
            return new Promise((resolve) => {
                img.onload = img.onerror = resolve;
            });
        })
    );

    html2pdf()
    .from(element)
    .set({
      filename: `Order-${orderId}.pdf`,
      html2canvas: { scale: 2, useCORS: true }, // enable CORS support
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    })
    .save();
};

// New function to print to a specific KOT printer
export const printToKOTPrinter = async (
    type: "main" | "kitchen",
    htmlContent: string,
    orderId: string
): Promise<KotPrintResult> => {
    const printerId = await getUserKOTPrinter(type);

    // If in React Native WebView, send printerId in the message
    if (isWebView()) {
        (window as any).ReactNativeWebView.postMessage(
            JSON.stringify({
                type: "print",
                printerType: type,
                printerId,
                data: htmlContent || "<p>Error...Error...</p>",
            })
        );
        return { channel: 'webview' };
    }

    // If in browser and printerId is available, try direct Bluetooth print
    if (printerId && printerId !== 'NO_PRINTER' && (window.navigator as any)?.bluetooth) {
        try {
            await handleBluetoothPrintToDevice(printerId, htmlContent, orderId);
            return { channel: 'bluetooth' };
        } catch (err) {
            // Fallback to browser print if Bluetooth fails
            console.error("Bluetooth print failed, falling back:");
        }
    }

    // Fallback: use current print logic
    handleBrowserPrintWithoutNewWindow(htmlContent, orderId);
    return { channel: 'browser' };
};
